import { ChevronTop, Delete } from "@monta/icons";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import type { CustomSelectProps } from "./types";

export const CustomSelect = (props: CustomSelectProps) => {
  const {
    options,
    placeholder = "Select an option",
    onChange,
    selectedOption,
    innerBeforeIcon,
    tabIndex,
    disabled = false,
    containerClassName,
    bodyClassName,
    hasClear
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const optionsRef = useRef<HTMLLIElement[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          selectOption(options[highlightedIndex]);
        } else {
          setIsOpen((prev) => !prev);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const selectOption = (option) => {
    if (onChange) {
      onChange(option);
    }
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const resetSelect = (e) => {
    e.stopPropagation();
    selectOption(null);
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest"
      });
    }
  }, [highlightedIndex]);

  return (
    <div
      className={clsx(styles["custom-select-container"], containerClassName)}
      ref={containerRef}
      onKeyDown={!disabled ? handleKeyDown : () => {}}
      tabIndex={!disabled ? tabIndex : undefined}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls="custom-select-listbox"
      aria-labelledby="custom-select-button"
    >
      <button
        type="button"
        className={clsx(styles["custom-select-button"])}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        id="custom-select-button"
        disabled={disabled}
      >
        <div className={styles["custom-select-button__info"]}>
          {!!innerBeforeIcon && <span>{innerBeforeIcon}</span>}
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
        </div>
        <div className={styles["custom-select-button__actions"]}>
          {hasClear && (
            <span>
              <Delete
                size="small"
                display={selectedOption ? "block" : "none"}
                onClick={resetSelect}
              />
            </span>
          )}
          <span
            className={clsx(
              styles["custom-select-button__actions__chevron"],
              isOpen
                ? styles["custom-select-button__actions__chevron__open"]
                : ""
            )}
          >
            <ChevronTop size="small" />
          </span>
        </div>
      </button>
      {isOpen && (
        <div className={styles["custom-select-dropdown__container"]}>
          <ul
            className={clsx(
              styles["custom-select-dropdown--list"],
              bodyClassName
            )}
            role="listbox"
            id="custom-select-listbox"
            aria-labelledby="custom-select-button"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={selectedOption?.value === option.value}
                className={clsx(
                  styles["custom-select-option"],
                  highlightedIndex === index
                    ? styles["custom-select-option__highlighted"]
                    : ""
                )}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                ref={((el) => el && (optionsRef.current[index] = el)) as any}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
